/**
 * Mapbox HTML builder for iframe-based map rendering.
 * 
 * CRITICAL DESIGN: The HTML is now STATIC (no marker data baked in).
 * Markers are added/updated via postMessage commands.
 * This prevents iframe reloads when opportunity data changes.
 * 
 * Commands:
 * - init_markers: Initial marker data load
 * - update_markers: Update marker data (add/remove/change)
 * - change_basemap: Switch map style
 * - select_marker: Highlight a marker
 * - fly_to: Animate to location
 * - fit_bounds: Fit view to bounds
 */

import { color } from '../../../shared/tokens/mobile';
import { BasemapStyle } from '../../../hooks/useMapData';

export const BASEMAP_STYLES: Record<BasemapStyle, string> = {
  outdoors: 'mapbox://styles/mapbox/outdoors-v12',
  streets: 'mapbox://styles/mapbox/streets-v12',
  satellite: 'mapbox://styles/mapbox/satellite-v9',
  'satellite-streets': 'mapbox://styles/mapbox/satellite-streets-v12',
  light: 'mapbox://styles/mapbox/light-v11',
  dark: 'mapbox://styles/mapbox/dark-v11',
};

interface BuildMapHtmlParams {
  mapboxToken: string;
  styleUrl: string;
  center: [number, number];
  zoom: number;
}

export function buildMapHtml(params: BuildMapHtmlParams): string {
  const { mapboxToken, styleUrl, center, zoom } = params;
  const basemapStylesJSON = JSON.stringify(BASEMAP_STYLES);
  
  // Primary color for pins - must be URL encoded for SVG data URI
  const pinColor = encodeURIComponent(color.primary);
  const pinStroke = encodeURIComponent(color.surface);
  
  return `<!DOCTYPE html>
<html>
<head>
  <meta charset="utf-8">
  <meta name="viewport" content="initial-scale=1,maximum-scale=1,user-scalable=no">
  <link href="https://api.mapbox.com/mapbox-gl-js/v3.0.1/mapbox-gl.css" rel="stylesheet">
  <script src="https://api.mapbox.com/mapbox-gl-js/v3.0.1/mapbox-gl.js"></script>
  <style>
    * { box-sizing: border-box; }
    body { margin: 0; padding: 0; overflow: hidden; }
    #map { position: absolute; top: 0; bottom: 0; width: 100%; }
    
    /* Hide Mapbox attribution and logo */
    .mapboxgl-ctrl-attrib { display: none !important; }
    .mapboxgl-ctrl-logo { display: none !important; }
    
    /* Hide default zoom controls - we use our own toolbar */
    .mapboxgl-ctrl-top-right { display: none !important; }
    
    /* Scale control styling - bottom right, subtle */
    .mapboxgl-ctrl-scale {
      background: rgba(255,255,255,0.85) !important;
      border: 1px solid rgba(0,0,0,0.1) !important;
      border-radius: 4px !important;
      font-size: 10px !important;
      padding: 2px 6px !important;
    }
    
    /* Marker styling - NO transform transition (causes lag during pan) */
    .map-marker {
      width: 32px;
      height: 40px;
      cursor: pointer;
      filter: drop-shadow(0 2px 3px rgba(0,0,0,0.3));
      /* Use will-change to optimize rendering during map movement */
      will-change: transform;
    }
    .map-marker svg {
      width: 100%;
      height: 100%;
      /* Apply transitions to SVG, not the marker container */
      transition: transform 100ms ease-out, filter 100ms ease-out;
    }
    .map-marker:hover svg {
      transform: scale(1.15);
      filter: drop-shadow(0 3px 5px rgba(0,0,0,0.35));
    }
    .map-marker.selected svg {
      transform: scale(1.3);
      filter: drop-shadow(0 4px 8px rgba(0,0,0,0.4));
    }
    .map-marker.selected {
      z-index: 100 !important;
    }
    
    /* Current location pulse */
    .location-marker {
      width: 18px;
      height: 18px;
      background: ${color.info};
      border: 3px solid white;
      border-radius: 50%;
      box-shadow: 0 0 0 3px rgba(74, 107, 130, 0.3), 0 2px 4px rgba(0,0,0,0.2);
    }
    
    /* Dropped pin for adding opportunity */
    .dropped-pin {
      width: 40px;
      height: 50px;
      cursor: pointer;
      filter: drop-shadow(0 3px 6px rgba(0,0,0,0.4));
      animation: dropIn 0.3s ease-out;
    }
    .dropped-pin svg {
      width: 100%;
      height: 100%;
    }
    .dropped-pin:hover svg {
      transform: scale(1.1);
    }
    @keyframes dropIn {
      0% { transform: translateY(-30px); opacity: 0; }
      60% { transform: translateY(5px); }
      100% { transform: translateY(0); opacity: 1; }
    }
    
    /* Pulse ring around dropped pin */
    .dropped-pin::after {
      content: '';
      position: absolute;
      top: 50%;
      left: 50%;
      width: 60px;
      height: 60px;
      margin: -30px 0 0 -30px;
      border: 2px solid ${color.primary};
      border-radius: 50%;
      animation: pulse 1.5s ease-out infinite;
      pointer-events: none;
    }
    @keyframes pulse {
      0% { transform: scale(0.5); opacity: 0.8; }
      100% { transform: scale(1.5); opacity: 0; }
    }
  </style>
</head>
<body>
  <div id="map"></div>
  <script>
    mapboxgl.accessToken = '${mapboxToken}';
    const BASEMAP_STYLES = ${basemapStylesJSON};
    const PIN_COLOR = decodeURIComponent('${pinColor}');
    const PIN_STROKE = decodeURIComponent('${pinStroke}');
    
    // State
    const markers = new Map();  // id -> { marker, element, data }
    let selectedId = null;
    let locationMarker = null;
    let droppedPinMarker = null;
    let droppedPinCoords = null;
    let mapReady = false;
    let pendingMarkers = null;
    let pendingParcels = null; // Queue parcels until map ready
    let lastParcelData = null; // Persist for basemap restore
    
    // Initialize map
    const map = new mapboxgl.Map({
      container: 'map',
      style: '${styleUrl}',
      center: [${center[0]}, ${center[1]}],
      zoom: ${zoom},
      attributionControl: false,
      dragRotate: false,
      pitchWithRotate: false
    });
    
    // Scale control bottom-left (away from FAB on right)
    map.addControl(new mapboxgl.ScaleControl({ unit: 'imperial' }), 'bottom-left');
    
    // Create SVG pin
    function createPinSVG(fillColor) {
      return \`<svg viewBox="0 0 32 40" xmlns="http://www.w3.org/2000/svg">
        <path d="M16 0C7.2 0 0 7.2 0 16c0 12 16 24 16 24s16-12 16-24C32 7.2 24.8 0 16 0z" 
              fill="\${fillColor}" stroke="\${PIN_STROKE}" stroke-width="2"/>
        <circle cx="16" cy="16" r="6" fill="\${PIN_STROKE}"/>
      </svg>\`;
    }
    
    // Add or update a marker
    function setMarker(id, coords, markerColor, portfolioId) {
      // Remove existing if updating
      if (markers.has(id)) {
        markers.get(id).marker.remove();
      }
      
      const el = document.createElement('div');
      el.className = 'map-marker';
      el.innerHTML = createPinSVG(markerColor);
      if (selectedId === id) el.classList.add('selected');
      
      el.addEventListener('click', (e) => {
        e.stopPropagation();
        selectMarker(id);
        // Ease to marker for better view
        map.easeTo({
          center: coords,
          zoom: Math.max(map.getZoom(), 12), // Don't zoom out, only in if needed
          duration: 500
        });
        parent.postMessage({ type: 'marker_click', id, portfolio_id: portfolioId }, '*');
      });
      
      const marker = new mapboxgl.Marker({ element: el, anchor: 'bottom' })
        .setLngLat(coords)
        .addTo(map);
      
      markers.set(id, { marker, element: el, data: { coords, color: markerColor, portfolioId } });
    }
    
    // Remove a marker
    function removeMarker(id) {
      if (markers.has(id)) {
        markers.get(id).marker.remove();
        markers.delete(id);
      }
    }
    
    // Select/deselect marker
    function selectMarker(id) {
      // Deselect previous
      if (selectedId && markers.has(selectedId)) {
        markers.get(selectedId).element.classList.remove('selected');
      }
      selectedId = id;
      // Select new
      if (id && markers.has(id)) {
        markers.get(id).element.classList.add('selected');
      }
    }
    
    // Update all markers from data
    function updateAllMarkers(markerData) {
      const newIds = new Set(markerData.map(m => m.id));
      
      // Remove markers not in new data
      for (const id of markers.keys()) {
        if (!newIds.has(id)) removeMarker(id);
      }
      
      // Add/update markers
      markerData.forEach(m => {
        setMarker(m.id, m.coords, m.color, m.portfolio_id);
      });
    }
    
    // Show current location
    function showLocation(lat, lng) {
      if (locationMarker) locationMarker.remove();
      const el = document.createElement('div');
      el.className = 'location-marker';
      locationMarker = new mapboxgl.Marker({ element: el })
        .setLngLat([lng, lat])
        .addTo(map);
    }
    
    // Create dropped pin SVG (different color to distinguish from existing markers)
    function createDroppedPinSVG() {
      return \`<svg viewBox="0 0 40 50" xmlns="http://www.w3.org/2000/svg">
        <path d="M20 0C9 0 0 9 0 20c0 15 20 30 20 30s20-15 20-30C40 9 31 0 20 0z" 
              fill="${color.warning}" stroke="${color.surface}" stroke-width="2.5"/>
        <circle cx="20" cy="20" r="8" fill="${color.surface}"/>
        <path d="M16 20h8M20 16v8" stroke="${color.warning}" stroke-width="2" stroke-linecap="round"/>
      </svg>\`;
    }
    
    // Show dropped pin at coordinates
    function showDroppedPin(lat, lng) {
      removeDroppedPin();
      droppedPinCoords = { lat, lng };
      
      const el = document.createElement('div');
      el.className = 'dropped-pin';
      el.innerHTML = createDroppedPinSVG();
      el.style.position = 'relative';
      
      // Click on dropped pin to confirm adding
      el.addEventListener('click', (e) => {
        e.stopPropagation();
        parent.postMessage({ 
          type: 'dropped_pin_click', 
          latitude: droppedPinCoords.lat, 
          longitude: droppedPinCoords.lng 
        }, '*');
      });
      
      droppedPinMarker = new mapboxgl.Marker({ element: el, anchor: 'bottom' })
        .setLngLat([lng, lat])
        .addTo(map);
    }
    
    // Remove dropped pin
    function removeDroppedPin() {
      if (droppedPinMarker) {
        droppedPinMarker.remove();
        droppedPinMarker = null;
        droppedPinCoords = null;
      }
    }
    
    // Message handler
    window.addEventListener('message', (event) => {
      const data = event.data;
      if (!data || !data.type) return;
      
      switch (data.type) {
        case 'init_markers':
        case 'update_markers':
          if (mapReady) {
            updateAllMarkers(data.markers || []);
          } else {
            pendingMarkers = data.markers || [];
          }
          break;
          
        case 'change_basemap':
          if (data.basemap && BASEMAP_STYLES[data.basemap]) {
            map.setStyle(BASEMAP_STYLES[data.basemap]);
          }
          break;
          
        case 'select_marker':
          selectMarker(data.id);
          break;
          
        case 'fly_to':
          map.flyTo({
            center: [data.lng, data.lat],
            zoom: data.zoom || 14,
            duration: 1000
          });
          if (data.showLocation) showLocation(data.lat, data.lng);
          break;
          
        case 'fit_bounds':
          if (data.bounds && data.bounds.length === 4) {
            map.fitBounds([[data.bounds[0], data.bounds[1]], [data.bounds[2], data.bounds[3]]], 
              { padding: 50, duration: 800 });
          }
          break;
          
        case 'zoom_in':
          map.zoomIn({ duration: 300 });
          break;
          
        case 'zoom_out':
          map.zoomOut({ duration: 300 });
          break;
          
        case 'show_dropped_pin':
          if (data.lat && data.lng) {
            showDroppedPin(data.lat, data.lng);
          }
          break;
          
        case 'remove_dropped_pin':
          removeDroppedPin();
          break;
          
        case 'show_parcels':
          if (mapReady) {
            showParcels(data.parcels || [], data.neighborsByTier || {});
          } else {
            pendingParcels = { parcels: data.parcels || [], neighborsByTier: data.neighborsByTier || {} };
          }
          lastParcelData = { parcels: data.parcels || [], neighborsByTier: data.neighborsByTier || {} };
          break;
          
        case 'hide_parcels':
          hideParcels();
          lastParcelData = null;
          break;
      }
    });
    
    // Parcel polygon layers - click for popup (like v1)
    // Stores parcel data for popup display
    let parcelDataMap = new Map();
    let currentPopup = null; // Track current popup to prevent duplicates
    let parcelClickHandlers = []; // Track event handlers to remove them properly
    
    function showParcels(parcels, neighborsByTier) {
      hideParcels(); // Clear existing (removes handlers and popups)
      parcelDataMap.clear();
      
      // Count total neighbors across all tiers
      const totalNeighbors = Object.values(neighborsByTier || {}).reduce((sum, arr) => sum + (arr?.length || 0), 0);
      if (!parcels.length && totalNeighbors === 0) return;
      
      // Tier colors: L1 = bright, L2 = medium, L3 = subtle
      const tierColors = {
        1: { fill: '#4A90E2', line: '#2E5C8A', outline: '#1A3A5C' }, // Blue - direct neighbors
        2: { fill: '#7B9ACC', line: '#5A7AA3', outline: '#3A5A7A' }, // Lighter blue - second ring
        3: { fill: '#A8B8D8', line: '#8797B7', outline: '#677797' }, // Lightest blue - third ring
      };
      
      // Helper: Parse number safely (API may return strings)
      function parseNum(val) {
        if (val == null) return null;
        const num = typeof val === 'string' ? parseFloat(val) : val;
        return isNaN(num) ? null : num;
      }
      
      // Helper: Format currency with K/M suffix
      function formatCurrency(num) {
        const n = parseNum(num);
        if (n == null) return 'N/A';
        if (n >= 1000000) return '$' + (n / 1000000).toFixed(1) + 'M';
        if (n >= 1000) return '$' + Math.round(n / 1000) + 'K';
        return '$' + Math.round(n).toLocaleString();
      }
      
      // Primary parcel: solid fill + thick border
      if (parcels.length > 0) {
        const primaryFeatures = parcels.filter(p => p.geometry).map(p => {
          // Store parcel data for popup
          parcelDataMap.set('primary-' + p.id, {
            type: 'primary',
            name: p.name || '',
            acreage: parseNum(p.acreage),
            taxValue: parseNum(p.taxValue),
          });
          
          return {
            type: 'Feature',
            geometry: p.geometry,
            properties: { 
              id: p.id, 
              dataKey: 'primary-' + p.id,
              type: 'primary' 
            }
          };
        });
        
        if (primaryFeatures.length > 0) {
          map.addSource('parcels-primary', {
            type: 'geojson',
            data: { type: 'FeatureCollection', features: primaryFeatures }
          });
        }
      }
      
      // Neighbor parcels by tier (L1/L2/L3) - from graph data
      const tierOrder = [1, 2, 3]; // Render L3 first (bottom), then L2, then L1 (top)
      
      tierOrder.forEach(tier => {
        const neighbors = neighborsByTier[tier] || [];
        if (neighbors.length === 0) return;
        
        const tierFeatures = neighbors.filter(n => n.geometry).map(n => {
          // Store parcel data for popup
          parcelDataMap.set('neighbor-' + tier + '-' + n.id, {
            type: 'neighbor',
            tier: tier,
            name: n.name || '',
            acreage: parseNum(n.acreage),
            taxValue: parseNum(n.taxValue),
          });
          
          return {
            type: 'Feature',
            geometry: n.geometry,
            properties: { 
              id: n.id, 
              dataKey: 'neighbor-' + tier + '-' + n.id,
              type: 'neighbor',
              tier: tier
            }
          };
        });
        
        if (tierFeatures.length > 0) {
          const sourceId = 'parcels-neighbors-l' + tier;
          map.addSource(sourceId, {
            type: 'geojson',
            data: { type: 'FeatureCollection', features: tierFeatures }
          });
        }
      });
      
      // LAYER ORDER: L3 fill -> L2 fill -> L1 fill -> primary fill -> L3 lines -> L2 lines -> L1 lines -> primary lines
      
      // Neighbor fills by tier (L3 bottom, L2 middle, L1 top) - for click detection
      tierOrder.forEach(tier => {
        const sourceId = 'parcels-neighbors-l' + tier;
        if (map.getSource(sourceId)) {
          map.addLayer({
            id: sourceId + '-fill',
            type: 'fill',
            source: sourceId,
            paint: { 'fill-color': '#000000', 'fill-opacity': 0 }
          });
        }
      });
      
      // Primary fill ON TOP of all neighbor fills (so subject parcel wins clicks)
      if (map.getSource('parcels-primary')) {
        map.addLayer({
          id: 'parcels-primary-fill',
          type: 'fill',
          source: 'parcels-primary',
          paint: { 'fill-color': '#000000', 'fill-opacity': 0 }
        });
      }
      
      // Neighbor border lines by tier (L3 bottom, L2 middle, L1 top) - different colors per tier
      tierOrder.forEach(tier => {
        const sourceId = 'parcels-neighbors-l' + tier;
        if (map.getSource(sourceId)) {
          const colors = tierColors[tier] || tierColors[1];
          map.addLayer({
            id: sourceId + '-line-outline',
            type: 'line',
            source: sourceId,
            paint: { 'line-color': colors.outline, 'line-width': 3, 'line-opacity': 0.5 }
          });
          map.addLayer({
            id: sourceId + '-line',
            type: 'line',
            source: sourceId,
            paint: { 'line-color': colors.line, 'line-width': 1.5, 'line-opacity': 0.9 }
          });
        }
      });
      
      // Primary border lines ON TOP (bright yellow)
      if (map.getSource('parcels-primary')) {
        map.addLayer({
          id: 'parcels-primary-line-outline',
          type: 'line',
          source: 'parcels-primary',
          paint: { 'line-color': '#000000', 'line-width': 5, 'line-opacity': 0.7 }
        });
        map.addLayer({
          id: 'parcels-primary-line',
          type: 'line',
          source: 'parcels-primary',
          paint: { 'line-color': '#FFD700', 'line-width': 3, 'line-opacity': 1 }
        });
      }
      
      // Add click handlers for parcel popups (like v1)
      function handleParcelClick(e) {
        if (!e.features || e.features.length === 0) return;
        
        // Close existing popup to prevent duplicates
        if (currentPopup) {
          currentPopup.remove();
          currentPopup = null;
        }
        
        const feature = e.features[0];
        const dataKey = feature.properties.dataKey;
        const data = parcelDataMap.get(dataKey);
        
        if (!data) return;
        
        const isPrimary = data.type === 'primary';
        const tier = data.tier || 1;
        const badgeColor = isPrimary ? '#B8860B' : (tierColors[tier] ? tierColors[tier].line : '#008B8B');
        const badgeLabel = isPrimary ? 'Subject Parcel' : (tier ? 'L' + tier + ' Neighbor' : 'Neighbor');
        
        // Format values
        const acreageStr = data.acreage ? data.acreage.toFixed(2) + ' ac' : 'N/A';
        const taxStr = formatCurrency(data.taxValue);
        const perAcreStr = (data.taxValue && data.acreage && data.acreage > 0)
          ? formatCurrency(Math.round(data.taxValue / data.acreage)) + '/ac'
          : 'N/A';
        
        const popupHtml = '<div style="font-family: system-ui, -apple-system, sans-serif; min-width: 180px;">' +
          '<div style="display: inline-block; padding: 2px 8px; border-radius: 4px; font-size: 10px; font-weight: 600; margin-bottom: 6px; background: ' + badgeColor + '20; color: ' + badgeColor + ';">' + badgeLabel + '</div>' +
          (data.name ? '<div style="font-size: 13px; font-weight: 600; margin-bottom: 4px; color: #1a1a1a;">' + data.name + '</div>' : '') +
          '<div style="font-size: 12px; color: #666; margin-top: 4px;"><strong>Acreage:</strong> ' + acreageStr + '</div>' +
          '<div style="font-size: 12px; color: #666; margin-top: 2px;"><strong>Tax Value:</strong> ' + taxStr + '</div>' +
          '<div style="font-size: 12px; color: #666; margin-top: 2px;"><strong>Per Acre:</strong> ' + perAcreStr + '</div>' +
          '</div>';
        
        // Create and store popup reference
        currentPopup = new mapboxgl.Popup({ offset: 10, maxWidth: '250px' })
          .setLngLat(e.lngLat)
          .setHTML(popupHtml)
          .addTo(map);
      }
      
      // Attach click handlers to parcel layers and track them
      if (map.getLayer('parcels-primary-fill')) {
        const primaryClickHandler = (e) => handleParcelClick(e);
        map.on('click', 'parcels-primary-fill', primaryClickHandler);
        parcelClickHandlers.push({ layer: 'parcels-primary-fill', event: 'click', handler: primaryClickHandler });
      }
      
      tierOrder.forEach(tier => {
        const layerId = 'parcels-neighbors-l' + tier + '-fill';
        if (map.getLayer(layerId)) {
          const clickHandler = (e) => handleParcelClick(e);
          map.on('click', layerId, clickHandler);
          parcelClickHandlers.push({ layer: layerId, event: 'click', handler: clickHandler });
          
          // Hover handlers
          const enterHandler = () => { map.getCanvas().style.cursor = 'pointer'; };
          const leaveHandler = () => { map.getCanvas().style.cursor = ''; };
          map.on('mouseenter', layerId, enterHandler);
          map.on('mouseleave', layerId, leaveHandler);
          parcelClickHandlers.push({ layer: layerId, event: 'mouseenter', handler: enterHandler });
          parcelClickHandlers.push({ layer: layerId, event: 'mouseleave', handler: leaveHandler });
        }
      });
      
      // Cursor change on hover for primary (only if layer exists)
      if (map.getLayer('parcels-primary-fill')) {
        const primaryEnterHandler = () => { map.getCanvas().style.cursor = 'pointer'; };
        const primaryLeaveHandler = () => { map.getCanvas().style.cursor = ''; };
        map.on('mouseenter', 'parcels-primary-fill', primaryEnterHandler);
        map.on('mouseleave', 'parcels-primary-fill', primaryLeaveHandler);
        parcelClickHandlers.push({ layer: 'parcels-primary-fill', event: 'mouseenter', handler: primaryEnterHandler });
        parcelClickHandlers.push({ layer: 'parcels-primary-fill', event: 'mouseleave', handler: primaryLeaveHandler });
      }
    }
    
    // Clear parcel layers
    function hideParcels() {
      // Close and remove popup
      if (currentPopup) {
        currentPopup.remove();
        currentPopup = null;
      }
      
      // Remove all event handlers first
      parcelClickHandlers.forEach(({ layer, event, handler }) => {
        if (map.getLayer(layer)) {
          map.off(event, layer, handler);
        }
      });
      parcelClickHandlers = [];
      
      // Remove all parcel layers (fills, lines, outlines)
      const layersToRemove = [
        'parcels-primary-fill', 
        'parcels-primary-line',
        'parcels-primary-line-outline',
      ];
      
      // Add tiered neighbor layers
      [1, 2, 3].forEach(tier => {
        layersToRemove.push(
          'parcels-neighbors-l' + tier + '-fill',
          'parcels-neighbors-l' + tier + '-line',
          'parcels-neighbors-l' + tier + '-line-outline'
        );
      });
      
      layersToRemove.forEach(layerId => {
        if (map.getLayer(layerId)) map.removeLayer(layerId);
      });
      
      // Remove all parcel sources
      const sourcesToRemove = ['parcels-primary'];
      [1, 2, 3].forEach(tier => {
        sourcesToRemove.push('parcels-neighbors-l' + tier);
      });
      
      sourcesToRemove.forEach(sourceId => {
        if (map.getSource(sourceId)) map.removeSource(sourceId);
      });
      
      // Clear stored data
      parcelDataMap.clear();
    }
    
    // Re-add markers and parcels after style change (basemap switch)
    map.on('style.load', () => {
      const currentMarkers = Array.from(markers.values()).map(m => ({
        id: [...markers].find(([k, v]) => v === m)[0],
        coords: m.data.coords,
        color: m.data.color,
        portfolio_id: m.data.portfolioId
      }));
      markers.clear();
      currentMarkers.forEach(m => setMarker(m.id, m.coords, m.color, m.portfolio_id));
      
      if (locationMarker) {
        const lngLat = locationMarker.getLngLat();
        locationMarker.remove();
        showLocation(lngLat.lat, lngLat.lng);
      }
      
      // Restore parcel overlays after style change
      if (lastParcelData) {
        showParcels(lastParcelData.parcels, lastParcelData.neighborsByTier);
      }
    });
    
    // Map ready
    map.on('load', () => {
      mapReady = true;
      
      // Process pending markers
      if (pendingMarkers) {
        updateAllMarkers(pendingMarkers);
        pendingMarkers = null;
      }
      
      // Process pending parcels (sent before map was ready)
      if (pendingParcels) {
        showParcels(pendingParcels.parcels, pendingParcels.neighborsByTier);
        pendingParcels = null;
      }
      
      // Click to deselect marker OR remove dropped pin
      map.on('click', (e) => {
        const target = e.originalEvent.target;
        if (target.closest('.map-marker')) return;
        if (target.closest('.dropped-pin')) return;
        
        // Don't deselect when clicking on parcel layers (let parcel popup show)
        const parcelLayers = [
          'parcels-primary-fill',
          'parcels-neighbors-l1-fill',
          'parcels-neighbors-l2-fill',
          'parcels-neighbors-l3-fill'
        ].filter(id => map.getLayer(id));
        const parcelFeatures = map.queryRenderedFeatures(e.point, {
          layers: parcelLayers
        });
        if (parcelFeatures.length > 0) return;
        
        // Remove dropped pin if clicking elsewhere
        if (droppedPinMarker) {
          removeDroppedPin();
          parent.postMessage({ type: 'dropped_pin_cancelled' }, '*');
          return;
        }
        
        selectMarker(null);
        parent.postMessage({ type: 'map_click' }, '*');
      });
      
      // Long press / right-click for adding - shows dropped pin
      let pressTimer = null;
      let pressCoords = null;
      
      map.on('mousedown', (e) => {
        if (e.originalEvent.target.closest('.map-marker')) return;
        if (e.originalEvent.target.closest('.dropped-pin')) return;
        pressCoords = e.lngLat;
        pressTimer = setTimeout(() => {
          if (pressCoords) {
            showDroppedPin(pressCoords.lat, pressCoords.lng);
            parent.postMessage({ type: 'long_press', latitude: pressCoords.lat, longitude: pressCoords.lng }, '*');
          }
        }, 600);
      });
      
      map.on('mouseup', () => { clearTimeout(pressTimer); pressTimer = null; });
      map.on('mousemove', () => { if (pressTimer) { clearTimeout(pressTimer); pressTimer = null; }});
      
      map.on('contextmenu', (e) => {
        if (e.originalEvent.target.closest('.map-marker')) return;
        if (e.originalEvent.target.closest('.dropped-pin')) return;
        e.preventDefault();
        showDroppedPin(e.lngLat.lat, e.lngLat.lng);
        parent.postMessage({ type: 'long_press', latitude: e.lngLat.lat, longitude: e.lngLat.lng }, '*');
      });
      
      // Notify ready
      parent.postMessage({ type: 'map_ready' }, '*');
      
      // Track map position changes - debounced to avoid spam
      let moveTimeout = null;
      map.on('moveend', () => {
        clearTimeout(moveTimeout);
        moveTimeout = setTimeout(() => {
          const center = map.getCenter();
          const zoom = map.getZoom();
          parent.postMessage({ 
            type: 'map_position_changed', 
            center: [center.lng, center.lat],
            zoom: zoom 
          }, '*');
        }, 300);
      });
    });
  </script>
</body>
</html>`;
}

